import ReuseModal from "../../../components/fragment/modal/ReuseModal";
import {
  RazorBadgeTwo,
  RazorCopyText,
  RazorTextTrim,
} from "../../../components/razor-reuse";
import { PlaidTransactionItem } from "../../../redux/type";
import { formatTime } from "../../../utils/Helper";
import {
  formatLabel,
  formatMoney,
  formatPaymentStatus,
  getBankPaymentBadge,
  getCustomerName,
  getOverallStatusBadge,
  getWalletCreditBadge,
} from "./plaidTransactionHelpers";

interface PlaidTransactionModalProps {
  onClose?: () => void;
  visible?: boolean;
  details?: PlaidTransactionItem | null;
}

function formatDate(value?: string | null) {
  return value ? formatTime(value) : "---";
}

// Unlike the Stripe modal, empty values are shown as "---" rather than
// hidden, so the modal always lists every field the API returns.
function DetailRow({
  label,
  value,
  copyable,
}: {
  label: string;
  value?: React.ReactNode;
  copyable?: string | null;
}) {
  const isEmpty = value === null || value === undefined || value === "";
  return (
    <div className="flex items-start justify-between gap-[16px] py-[6px]">
      <p className="text-[13px] text-[#758089] shrink-0">{label}</p>
      <div className="text-[13px] font-medium text-[#1E1E1E] text-right break-all">
        {copyable ? (
          <RazorCopyText
            textToCopy={copyable}
            reference={RazorTextTrim(copyable, 26)}
            outerCopy
          />
        ) : isEmpty ? (
          "---"
        ) : (
          value
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[8px] bg-[#F4F4F5] px-[16px] py-[12px]">
      <p className="text-[12px] font-bold uppercase tracking-wide text-[#A1A1AA] mb-[4px]">
        {title}
      </p>
      <div className="divide-y divide-[#E4E4E7]">{children}</div>
    </div>
  );
}

export default function PlaidTransactionModal({
  onClose,
  visible,
  details,
}: PlaidTransactionModalProps) {
  const bankPayment = details?.bank_payment;
  const walletCredit = details?.wallet_credit;

  return (
    <ReuseModal
      title="Plaid Transaction Details"
      visible={visible}
      onClose={onClose}
      hideBtn
    >
      <div className="space-y-[16px] -mt-[8px]">
        <div className="flex items-center justify-between gap-[12px]">
          {details?.reference ? (
            <RazorCopyText
              textToCopy={details.reference}
              reference={details.reference}
              outerCopy
            />
          ) : (
            <p className="text-[13px] font-medium text-[#1E1E1E]">---</p>
          )}
          <RazorBadgeTwo
            className={getOverallStatusBadge(details?.status)}
            text={formatLabel(details?.status)}
          />
        </div>

        <Section title="Overview">
          <DetailRow label="Reference" copyable={details?.reference} />
          <DetailRow
            label="Plaid Payment ID"
            copyable={details?.plaid_payment_id}
          />
          <DetailRow label="Transaction ID" copyable={details?.id} />
          <DetailRow label="Wallet Currency" value={details?.wallet_currency} />
          <DetailRow label="Status" value={formatLabel(details?.status)} />
          <DetailRow label="Created At" value={formatDate(details?.created_at)} />
          <DetailRow label="Updated At" value={formatDate(details?.updated_at)} />
        </Section>

        <Section title="Customer">
          <DetailRow label="Name" value={getCustomerName(details)} />
          <DetailRow label="Email" copyable={details?.customer?.email} />
          <DetailRow label="Customer ID" copyable={details?.customer?.id} />
        </Section>

        <Section title="Bank Payment">
          <DetailRow
            label="Status"
            value={
              bankPayment?.status ? (
                <RazorBadgeTwo
                  className={getBankPaymentBadge(bankPayment.status)}
                  text={formatPaymentStatus(bankPayment.status)}
                />
              ) : null
            }
          />
          <DetailRow
            label="Amount"
            value={formatMoney(bankPayment?.amount, bankPayment?.currency)}
          />
          <DetailRow label="Currency" value={bankPayment?.currency} />
          <DetailRow
            label="Adjusted Reference"
            copyable={bankPayment?.adjusted_reference}
          />
          <DetailRow
            label="Status Updated At"
            value={formatDate(bankPayment?.status_updated_at)}
          />
          <DetailRow label="Failure Code" value={bankPayment?.failure_code} />
          <DetailRow
            label="Failure Reason"
            value={bankPayment?.failure_reason}
          />
        </Section>

        <Section title="Wallet Credit">
          <DetailRow
            label="Status"
            value={
              walletCredit?.status ? (
                <RazorBadgeTwo
                  className={getWalletCreditBadge(walletCredit.status)}
                  text={formatLabel(walletCredit.status)}
                />
              ) : null
            }
          />
          <DetailRow
            label="Amount"
            value={formatMoney(walletCredit?.amount, walletCredit?.currency)}
          />
          <DetailRow label="Currency" value={walletCredit?.currency} />
          <DetailRow label="Wallet ID" copyable={walletCredit?.wallet_id} />
          <DetailRow
            label="Ledger Reference"
            copyable={walletCredit?.ledger_reference}
          />
          <DetailRow
            label="Credited At"
            value={formatDate(walletCredit?.credited_at)}
          />
        </Section>
      </div>
    </ReuseModal>
  );
}
